import { useParams, useNavigate } from "react-router";
import { useApi } from "@/hooks/useApi";
import { useEncryption } from "@/hooks/useEncryption";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ShareLink } from "@/components/auth/ShareLink";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { PointsChart } from "@/components/charts/PointsChart";
import { ListChecks, BookOpen, Gift, Skull, CalendarDays, MessageCircle, Pencil, History, ArrowLeft } from "lucide-react";
import { MarkdownText } from "@/components/shared/MarkdownText";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";

interface ClientDetail {
  _key: string;
  name: string;
  icon: string;
  description: string;
  is_active: boolean;
  has_passcode: boolean;
  points: number;
}

interface HistoryItem {
  type: string;
  points: number;
  created_at: string;
  entity_name: string;
  entity_icon: string;
  encrypted_note?: string;
  ref_name?: string;
  ref_icon?: string;
  streak_days?: number;
  streak_factor?: number;
}

function getEventStyle(type: string) {
  switch (type) {
    case "rule_broken":
      return { emoji: "⚠️", bg: "bg-app-danger/10", border: "border-l-app-danger" };
    case "chore_completed":
      return { emoji: "✅", bg: "bg-app-success/10", border: "border-l-app-success" };
    case "chore_overdue":
      return { emoji: "⏰", bg: "bg-app-warning/10", border: "border-l-app-warning" };
    case "rule_streak":
      return { emoji: "🔥", bg: "bg-app-info/10", border: "border-l-app-info" };
    case "punishment_redeemed":
      return { emoji: "💀", bg: "bg-app-accent/10", border: "border-l-app-accent" };
    case "reward_redeemed":
      return { emoji: "🎁", bg: "bg-app-accent/10", border: "border-l-app-accent" };
    case "punishment_reversed":
    case "reward_reversed":
      return { emoji: "↩️", bg: "bg-app-surface-raised", border: "border-l-app-border" };
    default:
      return { emoji: "📝", bg: "bg-app-surface-raised", border: "border-l-app-border" };
  }
}

function formatHistoryLabel(h: HistoryItem): string {
  const name = h.entity_name || "[Encrypted]";
  switch (h.type) {
    case "chore_completed": return `Completed ${name}`;
    case "chore_overdue": return `Overdue: ${name}`;
    case "rule_broken": return `Rule broken: ${name}${h.encrypted_note ? ` — ${h.encrypted_note}` : ""}`;
    case "rule_streak": return `${name} streak: ${h.streak_days}d (×${h.streak_factor})`;
    case "punishment_redeemed": return `Punishment: ${h.ref_name || name}`;
    case "reward_redeemed": return `Reward: ${h.ref_name || name}`;
    case "punishment_reversed": return `Reversed punishment: ${h.ref_name || name}`;
    case "reward_reversed": return `Reversed reward: ${h.ref_name || name}`;
    default: return h.type?.replace(/_/g, " ") || "Event";
  }
}

export function ClientManage() {
  const { jwt, clientId } = useParams();
  const navigate = useNavigate();
  const { decryptValue, decryptList, hasKey } = useEncryption(clientId);

  const { data: client, loading, error } = useApi<ClientDetail>(
    `/api/admin/${jwt}/clients/${clientId}`,
  );

  const { data: rawDashData } = useApi<{
    points: number;
    history: HistoryItem[];
  }>(`/api/admin/${jwt}/dashboard/${clientId}`);

  const [history, setHistory] = useState<HistoryItem[]>([]);

  // Decrypt history entity names
  useEffect(() => {
    if (!rawDashData?.history) return;
    (async () => {
      const decrypted = await Promise.all(
        rawDashData.history.map(async (h) => ({
          ...h,
          entity_name: h.entity_name ? await decryptValue(h.entity_name) : "",
          ref_name: h.ref_name ? await decryptValue(h.ref_name) : undefined,
          encrypted_note: h.encrypted_note ? await decryptValue(h.encrypted_note) : undefined,
        })),
      );
      setHistory(decrypted);
    })();
  }, [rawDashData, decryptValue]);

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-app-danger text-body p-4">{error}</p>;
  if (!client) return null;

  const recentActivity = history.slice(0, 10);

  const sections = [
    { label: "Chores", icon: ListChecks, path: "chores", desc: "Manage recurring tasks", bg: "bg-app-success/10", color: "text-app-success" },
    { label: "Rules", icon: BookOpen, path: "rules", desc: "Set household rules", bg: "bg-app-danger/10", color: "text-app-danger" },
    { label: "Rewards", icon: Gift, path: "rewards", desc: "Define earnable rewards", bg: "bg-app-info/10", color: "text-app-info" },
    { label: "Punishments", icon: Skull, path: "punishments", desc: "Set fun punishments", bg: "bg-app-warning/10", color: "text-app-warning" },
    { label: "Sessions", icon: CalendarDays, path: "sessions", desc: "Schedule sessions", bg: "bg-app-accent/10", color: "text-app-accent" },
    { label: "Messages", icon: MessageCircle, path: "messages", desc: "Chat with partner", bg: "bg-app-primary/10", color: "text-app-primary" },
  ];

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Back to clients list */}
      <button
        type="button"
        onClick={() => navigate(`/admin/${jwt}/clients`)}
        className="flex items-center gap-1.5 text-sm text-app-text-muted hover:text-app-text transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        All partners
      </button>

      {/* Client Info Card */}
      <Card className="border-0 shadow-lg overflow-hidden py-0">
        <div className="bg-linear-to-br from-app-primary/20 via-app-accent/10 to-transparent p-6 md:p-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-app-surface-raised shadow-md flex items-center justify-center">
              <span className="text-4xl md:text-5xl">{client.icon}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-xl md:text-2xl font-bold text-app-text">{client.name}</h1>
                <button
                  type="button"
                  onClick={() => navigate(`/admin/${jwt}/${clientId}/edit`)}
                  className="p-1.5 rounded-lg hover:bg-app-surface-raised transition-colors cursor-pointer"
                  title="Edit partner"
                >
                  <Pencil className="w-4 h-4 text-app-text-muted" />
                </button>
              </div>
              <MarkdownText text={client.description} className="text-sm text-app-text-muted mt-0.5" />
            </div>
            <div className="text-right shrink-0">
              <div className={`w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center ${client.points >= 0 ? "bg-app-success/20" : "bg-app-danger/20"}`}>
                <span className={`text-2xl md:text-3xl font-extrabold ${client.points >= 0 ? "text-app-success" : "text-app-danger"}`}>
                  {client.points}
                </span>
              </div>
              <p className="text-xs text-app-text-muted mt-1">points</p>
            </div>
          </div>
          {/* Quick Nav */}
          <div className="flex flex-wrap gap-1.5 mt-4">
            {sections.map((s) => (
              <button
                key={s.path}
                type="button"
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-app-surface/80 hover:bg-app-surface shadow-sm hover:shadow transition-all duration-150 cursor-pointer text-xs font-medium text-app-text"
                onClick={(e) => { e.stopPropagation(); navigate(`/admin/${jwt}/${clientId}/${s.path}`); }}
              >
                <s.icon className={`w-3.5 h-3.5 ${s.color}`} />
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Share Link (collapsed by default) */}
      <ShareLink
        url={`/client/${clientId}`}
        title="Partner Access Link"
        description={client.has_passcode ? "Passcode already set." : "Partner hasn't set their passcode yet."}
        defaultCollapsed
      />

      {/* Points Chart */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="text-base text-app-text-muted">📈 Points Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          <PointsChart history={history} height={200} />
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base text-app-text-muted">🕐 Recent Activity</CardTitle>
          <Button variant="ghost" size="sm" className="text-xs text-app-text-muted gap-1" onClick={() => navigate(`/admin/${jwt}/${clientId}/activity`)}>
            <History className="w-3.5 h-3.5" /> View All
          </Button>
        </CardHeader>
        <CardContent>
          {recentActivity.length === 0 ? (
            <div className="text-center py-6">
              <div className="text-4xl mb-2">📭</div>
              <p className="text-sm text-app-text-muted">No activity yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentActivity.map((h, i) => {
                const style = getEventStyle(h.type);
                return (
                  <div key={i} className={`flex items-center gap-3 p-2.5 rounded-lg border-l-4 ${style.border} ${style.bg} hover:opacity-90 transition-opacity`}>
                    <span className="text-lg shrink-0">{style.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-app-text truncate">{formatHistoryLabel(h)}</p>
                      <p className="text-xs text-app-text-muted">
                        {new Date(h.created_at).toLocaleDateString()} · {new Date(h.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                    <span className={`text-sm font-bold shrink-0 ${h.points >= 0 ? "text-app-success" : "text-app-danger"}`}>
                      {h.points >= 0 ? "+" : ""}{h.points}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
